/**
 * Pure projection for `/help` — Help → Guided tour.
 *
 * Turns the 17 seeded stops into list rows: a two-digit number, the route the
 * stop opens (shown so a presenter can see where each stop lands before
 * starting), and whether it is the stop the running or last-ended tour is on.
 *
 * Transformation layer (layer 3): no React, no side effects.
 *
 * @see src/versions/v1/modules/help/TourScene.tsx
 * @see docs/logic/help/tour.md
 */

import type { TourStop } from '../../shared/seed/tour';
import { routeForView } from '../../shared/oem-routes';

export interface TourRow {
  readonly id: string;
  readonly index: number;
  /** "01" … "17", as the demo numbered them. */
  readonly n: string;
  readonly title: string;
  readonly body: string;
  /** The route the stop navigates to. */
  readonly route: string;
  /** The stop the tour is on, when a tour is running. */
  readonly current: boolean;
}

export function tourRows(
  stops: readonly TourStop[],
  active: boolean,
  activeIndex: number,
): TourRow[] {
  return stops.map((stop, index) => ({
    id: stop.id,
    index,
    n: String(index + 1).padStart(2, '0'),
    title: stop.title,
    body: stop.body,
    // Same resolution as the tour engine's `tourRoute`, kept here so this
    // layer never imports the engine's React hooks.
    route: routeForView(stop.action.view, stop.action.sub) ?? '/',
    current: active && index === activeIndex,
  }));
}
